/**
 * Firestore sync hooks - Unified subscription + loading state management
 *
 * These hooks subscribe to Firestore collections/documents and populate Jotai atoms.
 * They return isLoading=true until the first snapshot arrives, enabling use in
 * initialization flows.
 *
 * Usage pattern:
 * ```tsx
 * const isLoadingFolders = useFoldersSync(isAuthenticated);
 * const isLoadingCollection = useCollectionSync(isAuthenticated);
 * const isReady = !isLoadingFolders && !isLoadingCollection;
 * ```
 */
import { useEffect, useRef, useState } from 'react';

import type { DocumentData, DocumentReference, DocumentSnapshot, Query, QuerySnapshot } from 'firebase/firestore';
import { onSnapshot } from 'firebase/firestore';
import type { WritableAtom } from 'jotai';
import { useSetAtom } from 'jotai';

// ============================================================================
// Types
// ============================================================================

export interface SyncState<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
}

export interface UseFirestoreCollectionSyncOptions<R> {
  /** Jotai atom to populate with sync state */
  atom: WritableAtom<SyncState<R>, [SyncState<R>], void>;
  /** Transform raw Firestore documents to typed data */
  transform: (docs: DocumentData[]) => R;
  /** Query factory - called when enabled becomes true */
  queryFactory: () => Query<DocumentData> | null;
  /** Whether sync is enabled (e.g., only when authenticated) */
  enabled: boolean;
}

export interface UseFirestoreDocumentSyncOptions<R> {
  /** Jotai atom to populate with sync state */
  atom: WritableAtom<SyncState<R | null>, [SyncState<R | null>], void>;
  /** Transform raw Firestore document to typed data */
  transform: (doc: DocumentData | null) => R | null;
  /** Document reference factory - called when enabled becomes true */
  docRefFactory: () => DocumentReference<DocumentData> | null;
  /** Whether sync is enabled (e.g., only when authenticated) */
  enabled: boolean;
}

// ============================================================================
// Collection Sync Hook
// ============================================================================

/**
 * Subscribe to a Firestore collection and populate a Jotai atom.
 * Returns true while loading (until first snapshot arrives).
 *
 * @example
 * ```tsx
 * const isLoading = useFirestoreCollectionSync({
 *   atom: foldersStateAtom,
 *   transform: (docs) => docs.map(d => ({ id: d.id, ...d })).sort(...),
 *   queryFactory: () => query(collection(firestore, 'users', userId, 'folders')),
 *   enabled: isAuthenticated,
 * });
 * ```
 */
export function useFirestoreCollectionSync<R>({ atom, transform, queryFactory, enabled }: UseFirestoreCollectionSyncOptions<R>): boolean {
  const setState = useSetAtom(atom);
  const [isLoading, setIsLoading] = useState(enabled);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const transformRef = useRef(transform);
  transformRef.current = transform;

  useEffect(() => {
    // Clean up any existing subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!enabled) {
      // Not enabled - set empty state and mark as not loading
      setState({ data: undefined, isLoading: false, error: null });
      setIsLoading(false);
      return;
    }

    const queryRef = queryFactory();
    if (!queryRef) {
      setState({ data: undefined, isLoading: false, error: null });
      setIsLoading(false);
      return;
    }

    // Mark as loading
    setIsLoading(true);

    const unsub = onSnapshot(
      queryRef,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const docs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const transformedData = transformRef.current(docs);

        setState({
          data: transformedData,
          isLoading: false,
          error: null,
        });
        setIsLoading(false);
      },
      (error: Error) => {
        console.error('Firestore collection sync error:', error);
        setState({
          data: undefined,
          isLoading: false,
          error,
        });
        setIsLoading(false);
      },
    );

    unsubscribeRef.current = unsub;

    return () => {
      unsub();
      unsubscribeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, setState]);

  return isLoading;
}

// ============================================================================
// Document Sync Hook
// ============================================================================

/**
 * Subscribe to a Firestore document and populate a Jotai atom.
 * Returns true while loading (until first snapshot arrives).
 *
 * @example
 * ```tsx
 * const isLoading = useFirestoreDocumentSync({
 *   atom: settingsStateAtom,
 *   transform: (doc) => doc?.settings ?? DEFAULT_SETTINGS,
 *   docRefFactory: () => doc(firestore, 'users', userId),
 *   enabled: isAuthenticated,
 * });
 * ```
 */
export function useFirestoreDocumentSync<R>({ atom, transform, docRefFactory, enabled }: UseFirestoreDocumentSyncOptions<R>): boolean {
  const setState = useSetAtom(atom);
  const [isLoading, setIsLoading] = useState(enabled);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const transformRef = useRef(transform);
  transformRef.current = transform;

  useEffect(() => {
    // Clean up any existing subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!enabled) {
      // Not enabled - set empty state and mark as not loading
      setState({ data: undefined, isLoading: false, error: null });
      setIsLoading(false);
      return;
    }

    const docRef = docRefFactory();
    if (!docRef) {
      setState({ data: undefined, isLoading: false, error: null });
      setIsLoading(false);
      return;
    }

    // Mark as loading
    setIsLoading(true);

    const unsub = onSnapshot(
      docRef,
      (snapshot: DocumentSnapshot<DocumentData>) => {
        const docData = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
        const transformedData = transformRef.current(docData);

        setState({
          data: transformedData,
          isLoading: false,
          error: null,
        });
        setIsLoading(false);
      },
      (error: Error) => {
        console.error('Firestore document sync error:', error);
        setState({
          data: undefined,
          isLoading: false,
          error,
        });
        setIsLoading(false);
      },
    );

    unsubscribeRef.current = unsub;

    return () => {
      unsub();
      unsubscribeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, setState]);

  return isLoading;
}
